import type { Storage as TourKitStorage } from '@tour-kit/core';
import {
  AnnouncementsProvider,
  HeadlessModal,
  useAnnouncement,
  type AnnouncementConfig,
  type DismissalReason,
} from '@tour-kit/announcements';
import { Button, cn } from '@up2stay/up2promo-ui';
import { X } from 'lucide-react';
import { useEffect, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import {
  ANNOUNCEMENT_CATALOG,
  announcementCopyMap,
  type AnnouncementCopy,
  type GuidanceAnnouncementDef,
} from '@/core/guidance/announcements-registry';
import { PublishGuidanceAnnouncements } from '@/core/guidance/guidance-announcements-bridge';
import { trackGuidanceEvent } from '@/core/guidance/guidance-analytics';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';

import '@tour-kit/announcements/styles/variables.css';

type GuidanceAnnouncementsProviderProps = {
  children: ReactNode;
  storage: TourKitStorage;
};

/** AnnouncementsProvider types `storage` as DOM Storage; TourKit only needs get/set/remove. */
function asAnnouncementStorage(storage: TourKitStorage): globalThis.Storage {
  return {
    get length() {
      return 0;
    },
    clear() {},
    key() {
      return null;
    },
    getItem: (key) => {
      const value = storage.getItem(key);
      return typeof value === 'string' || value === null ? value : null;
    },
    setItem: (key, value) => {
      storage.setItem(key, value);
    },
    removeItem: (key) => {
      storage.removeItem(key);
    },
  };
}

function buildConfigs(
  catalog: readonly GuidanceAnnouncementDef[],
  copy: Record<string, AnnouncementCopy>,
  canAutoShow: boolean,
  navigateShell: (path: string) => void,
  dismissLabel: string,
): AnnouncementConfig[] {
  return catalog.flatMap((entry) => {
    const labels = copy[entry.id];
    if (!labels) return [];

    const config: AnnouncementConfig = {
      id: entry.id,
      variant: entry.variant,
      priority: entry.priority ?? 'normal',
      title: labels.title,
      description: labels.body,
      frequency: entry.frequency,
      autoShow: canAutoShow && entry.enabled,
      category: entry.kind,
      metadata: {
        kind: entry.kind,
        publishedAt: entry.publishedAt,
      },
      modalOptions: {
        size: 'md',
        closeOnOverlayClick: true,
        closeOnEscape: true,
        showCloseButton: true,
      },
      secondaryAction: {
        label: dismissLabel,
        variant: 'ghost',
        dismissOnClick: true,
      },
    };

    if (entry.ctaPath && labels.cta) {
      config.primaryAction = {
        label: labels.cta,
        dismissOnClick: true,
        onClick: () => {
          navigateShell(entry.ctaPath!);
        },
      };
    }

    return [config];
  });
}

type GuidanceAnnouncementModalProps = {
  id: string;
  title: string;
  body: string;
  ctaLabel?: string;
  dismissLabel: string;
  gotItLabel: string;
  kicker?: string;
};

function GuidanceAnnouncementModal({
  id,
  title,
  body,
  ctaLabel,
  dismissLabel,
  gotItLabel,
  kicker,
}: GuidanceAnnouncementModalProps) {
  return (
    <HeadlessModal id={id}>
      {({ open, config, dismiss, complete, overlayProps, contentProps }) => {
        if (!open || !config || typeof document === 'undefined') return null;

        return createPortal(
          <div className="guidance-announcement fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" {...overlayProps} />
            <div
              {...contentProps}
              className={cn(
                'guidance-announcement-card relative z-10',
                'w-[min(24rem,calc(100vw-2rem))]',
                'rounded-[var(--radius-surface,1rem)] border border-border/80',
                'bg-card text-card-foreground shadow-md outline-none',
                'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200',
              )}
            >
              <button
                type="button"
                className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={dismissLabel}
                onClick={() => dismiss('close_button')}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
              <div className="space-y-3 px-5 pb-5 pt-5 pr-12">
                {kicker ? (
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {kicker}
                  </p>
                ) : null}
                <h2
                  id={contentProps['aria-labelledby']}
                  className="text-lg font-semibold tracking-tight"
                >
                  {title}
                </h2>
                <p className="text-sm text-muted-foreground">{body}</p>
                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                  {config.secondaryAction ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => dismiss('secondary_action')}
                    >
                      {config.secondaryAction.label}
                    </Button>
                  ) : null}
                  {config.primaryAction && ctaLabel ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        config.primaryAction?.onClick?.();
                        complete();
                        if (config.primaryAction?.dismissOnClick !== false) {
                          dismiss('primary_action');
                        }
                      }}
                    >
                      {ctaLabel}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => dismiss('primary_action')}
                    >
                      {gotItLabel}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        );
      }}
    </HeadlessModal>
  );
}

function GuidanceAnnouncementsSurfaces() {
  const dict = useDictionary();
  const copy = useMemo(
    () => announcementCopyMap(dict, ANNOUNCEMENT_CATALOG),
    [dict],
  );
  const a = dict.guidance.announcements;

  return (
    <>
      {ANNOUNCEMENT_CATALOG.filter(
        (entry) =>
          (entry.variant === 'modal' || entry.variant === 'slideout') &&
          copy[entry.id],
      ).map((entry) => (
        <GuidanceAnnouncementModal
          key={entry.id}
          id={entry.id}
          title={copy[entry.id]!.title}
          body={copy[entry.id]!.body}
          ctaLabel={copy[entry.id]!.cta}
          dismissLabel={a.dismiss}
          gotItLabel={a.gotIt}
          kicker={entry.kind === 'ann.release' ? a.releaseKicker : undefined}
        />
      ))}
    </>
  );
}

/**
 * Hides interrupting announcements while a tour is active or the get-started
 * panel is open (soft delay — hide, do not dismiss).
 */
function AnnouncementHideWhenBlocked({
  id,
  blocked,
}: {
  id: string;
  blocked: boolean;
}) {
  const { isVisible, hide, show, canShow, isDismissed } = useAnnouncement(id);

  useEffect(() => {
    if (blocked && isVisible) {
      hide();
      return;
    }
    if (!blocked && canShow && !isDismissed && !isVisible) {
      const def = ANNOUNCEMENT_CATALOG.find((entry) => entry.id === id);
      if (def?.enabled) show();
    }
  }, [blocked, isVisible, hide, show, canShow, isDismissed, id]);

  return null;
}

function GuidanceAnnouncementsGate() {
  const isTourActive = useGuidanceStore((s) => s.isTourActive);
  const isChecklistPanelOpen = useGuidanceStore((s) => s.isChecklistPanelOpen);
  const blocked = isTourActive || isChecklistPanelOpen;

  return (
    <>
      {ANNOUNCEMENT_CATALOG.map((entry) => (
        <AnnouncementHideWhenBlocked
          key={entry.id}
          id={entry.id}
          blocked={blocked}
        />
      ))}
    </>
  );
}

export function GuidanceAnnouncementsProvider({
  children,
  storage,
}: GuidanceAnnouncementsProviderProps) {
  const dict = useDictionary();
  const copy = useMemo(
    () => announcementCopyMap(dict, ANNOUNCEMENT_CATALOG),
    [dict],
  );
  const { navigateShell } = useNavigateShell();
  const isTourActive = useGuidanceStore((s) => s.isTourActive);
  const isChecklistPanelOpen = useGuidanceStore((s) => s.isChecklistPanelOpen);
  const canAutoShow = !isTourActive && !isChecklistPanelOpen;

  const configs = useMemo(
    () =>
      buildConfigs(
        ANNOUNCEMENT_CATALOG,
        copy,
        canAutoShow,
        navigateShell,
        dict.guidance.announcements.dismiss,
      ),
    [copy, canAutoShow, navigateShell, dict.guidance.announcements.dismiss],
  );

  return (
    <AnnouncementsProvider
      announcements={configs}
      storage={asAnnouncementStorage(storage)}
      storageKey="announcements"
      queueConfig={{
        maxConcurrent: 1,
        autoShow: canAutoShow,
        delayBetween: 400,
      }}
      onAnnouncementShow={(id) => {
        trackGuidanceEvent({
          name: 'announcement_shown',
          metadata: { announcementId: id },
        });
      }}
      onAnnouncementDismiss={(id, reason: DismissalReason) => {
        trackGuidanceEvent({
          name: 'announcement_dismissed',
          metadata: { announcementId: id, reason },
        });
      }}
      onAnnouncementComplete={(id) => {
        trackGuidanceEvent({
          name: 'announcement_completed',
          metadata: { announcementId: id },
        });
      }}
    >
      {children}
      <PublishGuidanceAnnouncements />
      <GuidanceAnnouncementsGate />
      <GuidanceAnnouncementsSurfaces />
    </AnnouncementsProvider>
  );
}
